import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import { songImageUrl, extractObjectId, formatViewCount } from "../../../utils/utils";
import MultiGenreSelect from "../../../components/common/MultiGenreSelect";
import PageTargetSelector from "../../../components/common/PageTargetSelector";
import usePageTargetSelection from "../../../hooks/usePageTargetSelection";
import "./PopularSongs.css";
import {
  FaFire,
  FaMusic,
  FaSearch,
  FaUser,
  FaCompactDisc,
  FaPlay,
  FaEye,
  FaTrash,
  FaPlus,
  FaChevronLeft,
  FaChevronRight
} from "react-icons/fa";

const PopularSongs = () => {
  const navigate = useNavigate();
  const pageTarget = usePageTargetSelection();
  const targetParams = pageTarget.selectedTargetParams;
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [songsData, setSongsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [songToDelete, setSongToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [genres, setGenres] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [expandedGenres, setExpandedGenres] = useState({});

  const fetchGenres = async () => {
    try {
      const response = await allAPiServicesCall.getGenres(
        { page: 1, limit: 1000 },
        getAuthConfig()
      );
      if (response?.success && Array.isArray(response.data)) {
        setGenres(response.data);
      }
    } catch (error) {
      console.error("Error fetching genres:", error);
    }
  };

  useEffect(() => {
    fetchGenres();
  }, []);

  const fetchPopularSongs = async (page, limit) => {
    try {
      const response = await allAPiServicesCall.getPopularSongs(
        { page, limit, ...targetParams },
        getAuthConfig()
      );
      const { success, data } = response;

      if (success && Array.isArray(data.songs)) {
        setSongsData(data.songs);
        setTotalPages(data.totalPages);
        setTotalCount(data.totalCount || data.songs.length);
      } else {
        console.warn("No data found or data is not an array");
      }
    } catch (error) {
      console.error("Error fetching popular songs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchResults = async (song_name) => {
    setLoading(true);
    try {
      let response = await allAPiServicesCall.getSearchSong(
        { song_name },
        getAuthConfig()
      );

      const searchList = response.data || [];
      if (searchList.length > 0) {
        setSearchResults(searchList);
      } else {
        setSearchResults([]);
        toast.info("No results found");
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
      toast.error("Failed to fetch search results");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToMostPopular = async (song_id) => {
    const genreArray = selectedGenres[song_id] || [];
    const genre = genreArray.length > 0 ? genreArray.join(", ") : null;
    try {
      setLoading(true);
      const popularSong = await allAPiServicesCall.addMostPopularSongs(
        { song_id, genre, ...targetParams },
        getAuthConfig()
      );

      if (popularSong && popularSong.success) {
        toast.success(popularSong.message);
        setSearchTerm("");
        setSelectedGenres({});
        fetchPopularSongs(currentPage, limit);
      }
    } catch (error) {
      toast.error("An error occurred while adding the song. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenreSelect = (songId, genreArray) => {
    setSelectedGenres((prev) => ({
      ...prev,
      [songId]: genreArray,
    }));
  };

  const handleDelete = (songId) => {
    setSongToDelete(songId);
    setShowModal(true);
  };

  const confirmDelete = async () => {
    try {
      const updatedSongs = await allAPiServicesCall.deleteMostPopularSongs(
        { id: songToDelete },
        getAuthConfig()
      );
      if (updatedSongs && updatedSongs.success) {
        toast.success(updatedSongs.message);
        fetchPopularSongs(currentPage, limit);
      } else {
        toast.error(updatedSongs.message);
      }
    } catch (error) {
      toast.error("An error occurred while deleting the song. Please try again.");
    } finally {
      setShowModal(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  useEffect(() => {
    if (!searchTerm.trim()) {
      fetchPopularSongs(currentPage, limit);
      setSearchResults([]);
    }
  }, [searchTerm, currentPage, limit, targetParams.pageType, targetParams.genreSlug]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 2000);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedTerm) {
      fetchSearchResults(debouncedTerm);
    }
  }, [debouncedTerm]);

  const handleSearchTermChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  return (
    <CommonLayout>
      <div className="popular-songs-page">
        {/* Header Section */}
        <div className="popular-header">
          <div className="popular-header-content">
            <div className="popular-header-icon">
              <FaFire />
            </div>
            <div className="popular-header-text">
              <h1 className="popular-title">Most Popular Songs</h1>
              <p className="popular-subtitle">
                Manage and showcase trending songs for your users
              </p>
            </div>
          </div>
          <div className="popular-stats">
            <div className="popular-stat-card">
              <FaMusic className="popular-stat-icon" />
              <div className="popular-stat-info">
                <span className="popular-stat-value">{totalCount}</span>
                <span className="popular-stat-label">Total Songs</span>
              </div>
            </div>
          </div>
        </div>

        <PageTargetSelector
          pageTarget={pageTarget}
          subtitle="Switch between Homepage and Genre targets before curating most popular songs."
        />

        {/* Search Controls */}
        <div className="popular-controls">
          <div className="popular-search-wrapper">
            <FaSearch className="popular-search-icon" />
            <input
              type="text"
              className="popular-search-input"
              placeholder="Search for a song to add to popular list..."
              value={searchTerm}
              onChange={handleSearchTermChange}
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="popular-loading">
            <div className="popular-loading-spinner">
              <Spinner animation="border" />
            </div>
            <p className="popular-loading-text">Loading songs...</p>
          </div>
        ) : searchResults.length > 0 ? (
          <>
            {/* Search Results Section */}
            <div className="popular-section-header">
              <div className="popular-section-icon popular-section-icon-search">
                <FaSearch />
              </div>
              <h2 className="popular-section-title">Search Results</h2>
              <span className="popular-section-count">
                {searchResults.length} found
              </span>
            </div>
            <div className="popular-grid">
              {searchResults.map((song, index) => (
                <div key={index} className="popular-card popular-card-search">
                  <div className="popular-card-image-wrapper">
                    <img
                      src={songImageUrl(song)}
                      alt={song.song_name}
                      className="popular-card-image"
                    />
                    <div className="popular-card-overlay">
                      <div className="popular-card-play">
                        <FaPlay />
                      </div>
                    </div>
                    <div className="popular-card-badge popular-card-badge-search">
                      <FaSearch /> Result
                    </div>
                  </div>
                  <div className="popular-card-content">
                    <h3 className="popular-card-title">{song.song_name}</h3>
                    <div className="popular-card-meta">
                      <div className="popular-card-meta-item">
                        <FaUser className="popular-card-meta-icon" />
                        <span className="popular-card-meta-value">
                          {song?.artist?.artist_name || "Unknown Artist"}
                        </span>
                      </div>
                      {formatViewCount(song.popularity ?? song.playCount ?? song.totalPlays) && (
                        <div className="popular-card-meta-item">
                          <FaPlay className="popular-card-meta-icon" />
                          <span className="popular-card-meta-value">
                            {formatViewCount(song.popularity ?? song.playCount ?? song.totalPlays)} plays
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="popular-genre-select">
                      <MultiGenreSelect
                        genres={genres}
                        selectedGenres={selectedGenres[song.song_id] || []}
                        onChange={(genreArray) =>
                          handleGenreSelect(song.song_id, genreArray)
                        }
                        placeholder="Select Genres (Optional)"
                        size="sm"
                      />
                    </div>
                    <div className="popular-card-actions">
                      <button
                        className="popular-action-btn popular-action-btn-add"
                        onClick={() => handleAddToMostPopular(song.song_id)}
                      >
                        <FaPlus /> Add to Popular
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : songsData.length === 0 ? (
          <div className="popular-empty">
            <div className="popular-empty-icon">
              <FaMusic />
            </div>
            <h3 className="popular-empty-title">No Popular Songs</h3>
            <p className="popular-empty-text">
              Search for songs above to add them to your popular list
            </p>
          </div>
        ) : (
          <>
            {/* Popular Songs Grid */}
            <div className="popular-grid">
              {songsData
                .filter((song) =>
                  song.song_name
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
                )
                .map((song, index) => (
                  <div key={index} className="popular-card">
                    <div className="popular-card-image-wrapper">
                      <img
                        src={songImageUrl(song)}
                        alt={song.song_name}
                        className="popular-card-image"
                      />
                      <div className="popular-card-overlay">
                        <div className="popular-card-play">
                          <FaPlay />
                        </div>
                      </div>
                      <div className="popular-card-badge">
                        <FaFire /> Popular
                      </div>
                    </div>
                    <div className="popular-card-content">
                      <h3 className="popular-card-title">{song.song_name}</h3>
                      <div className="popular-card-meta">
                        <div className="popular-card-meta-item">
                          <FaUser className="popular-card-meta-icon" />
                          <span className="popular-card-meta-value">
                            {song.artist?.artist_name || "Unknown Artist"}
                          </span>
                        </div>
                        <div className="popular-card-meta-item">
                          <FaCompactDisc className="popular-card-meta-icon" />
                          <span className="popular-card-meta-value">
                            {song.album?.album_name || "No Album"}
                          </span>
                        </div>
                        {formatViewCount(song.popularity ?? song.playCount ?? song.totalPlays) && (
                          <div className="popular-card-meta-item">
                            <FaPlay className="popular-card-meta-icon" />
                            <span className="popular-card-meta-value">
                              {formatViewCount(song.popularity ?? song.playCount ?? song.totalPlays)} plays
                            </span>
                          </div>
                        )}
                      </div>
                      {song.genre && (() => {
                        const genreList = song.genre.split(", ").map(g => g.trim());
                        const isExpanded = expandedGenres[song._id];
                        const visibleGenres = isExpanded ? genreList : genreList.slice(0, 4);
                        const hasMore = genreList.length > 4;

                        return (
                          <div className="popular-card-genres">
                            {visibleGenres.map((g, i) => (
                              <span key={i} className="popular-card-genre">
                                {g}
                              </span>
                            ))}
                            {hasMore && (
                              <button
                                className="popular-genre-toggle"
                                onClick={() => setExpandedGenres(prev => ({
                                  ...prev,
                                  [song._id]: !prev[song._id]
                                }))}
                              >
                                {isExpanded ? "Show less" : `+${genreList.length - 4} more`}
                              </button>
                            )}
                          </div>
                        );
                      })()}
                      <div className="popular-card-actions">
                        <button
                          className="popular-action-btn popular-action-btn-view"
                          onClick={() => navigate(`/lyrics/${song.song_id}`)}
                        >
                          <FaEye /> View
                        </button>
                        <button
                          className="popular-action-btn popular-action-btn-delete"
                          onClick={() => handleDelete(extractObjectId(song._id))}
                        >
                          <FaTrash /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="popular-pagination">
                <button
                  className="popular-pagination-btn"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft /> Previous
                </button>
                <div className="popular-pagination-info">
                  <span className="popular-pagination-current">{currentPage}</span>
                  <span className="popular-pagination-separator">/</span>
                  <span className="popular-pagination-total">{totalPages}</span>
                </div>
                <button
                  className="popular-pagination-btn"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next <FaChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showModal}
        handleClose={closeModal}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this song from the popular list?"
      />
    </CommonLayout>
  );
};

export default PopularSongs;
